import React, { useState, useMemo } from 'react';
import {
  MessageSquare,
  ChevronRight,
  X,
  Save,
  Filter,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { updateDocument, deleteDocument } from '../../lib/firestore-helpers';
import type { ContactRequest, RequestType, RequestStatus } from '../../types/firebase';

const STATUS_LABELS: Record<RequestStatus, string> = {
  new: 'Nouvelle',
  read: 'Lue',
  processed: 'Traitee',
};

const STATUS_COLORS: Record<RequestStatus, string> = {
  new: 'bg-red-100 text-red-700',
  read: 'bg-yellow-100 text-yellow-700',
  processed: 'bg-green-100 text-green-700',
};

const TYPE_LABELS: Record<RequestType, string> = {
  contact: 'Contact',
  quote: 'Devis',
  custom: 'Sur-mesure',
};

interface RequestsTabProps {
  newCount?: number;
}

const RequestsTab: React.FC<RequestsTabProps> = () => {
  const { data: requests } = useFirestoreCollection<ContactRequest>({
    collection: 'requests',
    orderBy: ['createdAt', 'desc'],
    realtime: true,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<RequestType | ''>('');
  const [statusFilter, setStatusFilter] = useState<RequestStatus | ''>('');
  const [noteText, setNoteText] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const filtered = useMemo(() => {
    let result = requests;
    if (typeFilter) result = result.filter((r) => r.type === typeFilter);
    if (statusFilter) result = result.filter((r) => r.status === statusFilter);
    return result;
  }, [requests, typeFilter, statusFilter]);

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return requests.find((r) => r.id === selectedId) || null;
  }, [requests, selectedId]);

  const handleStatusChange = async (req: ContactRequest, newStatus: RequestStatus) => {
    try {
      await updateDocument('requests', req.id, { status: newStatus });
      toast.success(`Statut mis a jour: ${STATUS_LABELS[newStatus]}`);
    } catch {
      toast.error('Erreur mise a jour statut');
    }
  };

  const handleSaveNote = async () => {
    if (!selected) return;
    try {
      await updateDocument('requests', selected.id, { notes: noteText });
      toast.success('Note sauvegardee');
    } catch {
      toast.error('Erreur sauvegarde note');
    }
  };

  const formatDate = (ts: { seconds: number } | null | undefined) => {
    if (!ts) return '--';
    return new Date(ts.seconds * 1000).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDocument('requests', id);
      if (selectedId === id) setSelectedId(null);
      setConfirmDeleteId(null);
      toast.success('Demande supprimee');
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handlePurgeProcessed = async () => {
    const processed = requests.filter((r) => r.status === 'processed');
    if (processed.length === 0) return;
    try {
      await Promise.all(processed.map((r) => deleteDocument('requests', r.id)));
      setSelectedId(null);
      setConfirmPurge(false);
      toast.success(`${processed.length} demande(s) traitee(s) supprimee(s)`);
    } catch {
      toast.error('Erreur lors de la purge');
    }
  };

  const openDetail = (req: ContactRequest) => {
    setSelectedId(req.id);
    setNoteText(req.notes || '');
    setConfirmDeleteId(null);
    // Auto-mark as read if new
    if (req.status === 'new') {
      handleStatusChange(req, 'read');
    }
  };

  const processedCount = requests.filter((r) => r.status === 'processed').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Demandes clients</h2>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as RequestType | '')}
            className="bg-gray-100 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Tous les types</option>
            <option value="contact">Contact</option>
            <option value="quote">Devis</option>
            <option value="custom">Sur-mesure</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RequestStatus | '')}
            className="bg-gray-100 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Tous les statuts</option>
            <option value="new">Nouvelle</option>
            <option value="read">Lue</option>
            <option value="processed">Traitee</option>
          </select>
          {processedCount > 0 && !confirmPurge && (
            <button
              onClick={() => setConfirmPurge(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Purger traitees ({processedCount})
            </button>
          )}
          {confirmPurge && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600 font-medium">Supprimer {processedCount} traitee(s) ?</span>
              <button
                onClick={handlePurgeProcessed}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirmer
              </button>
              <button
                onClick={() => setConfirmPurge(false)}
                className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-6">
        {/* List */}
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {filtered.length === 0 && (
            <div className="bg-white rounded-xl shadow p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune demande</p>
            </div>
          )}

          {filtered.map((req) => (
            <div
              key={req.id}
              onClick={() => openDetail(req)}
              className={`bg-white rounded-xl shadow p-4 cursor-pointer hover:shadow-md transition-shadow flex items-center gap-4 ${
                selectedId === req.id ? 'ring-2 ring-red-500' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[req.status]}`}>
                    {STATUS_LABELS[req.status]}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {TYPE_LABELS[req.type] || req.type}
                  </span>
                </div>
                <p className="text-sm text-gray-900 mt-1 truncate">
                  {(req.data?.name as string) || (req.data?.email as string) || `Demande #${req.id.slice(0, 6)}`}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(req.createdAt)}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg p-6 sticky top-32 h-fit max-h-[calc(100vh-12rem)] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Detail de la demande</h3>
              <button
                onClick={() => setSelectedId(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status + type */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[selected.status]}`}>
                {STATUS_LABELS[selected.status]}
              </span>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {TYPE_LABELS[selected.type] || selected.type}
              </span>
              <span className="text-xs text-gray-400">{formatDate(selected.createdAt)}</span>
            </div>

            {/* Data */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Donnees</h4>
              <dl className="space-y-2">
                {Object.entries(selected.data || {}).map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <dt className="text-xs font-medium text-gray-500 min-w-[100px]">{key}</dt>
                    <dd className="text-xs text-gray-900 break-all">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Status change */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Changer le statut</h4>
              <div className="flex gap-2">
                {(['new', 'read', 'processed'] as RequestStatus[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(selected, s)}
                    className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                      selected.status === s
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Note interne</h4>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none resize-y"
                placeholder="Ajouter une note..."
              />
              <div className="flex justify-between mt-2">
                {confirmDeleteId === selected.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-600">Confirmer ?</span>
                    <button
                      onClick={() => handleDelete(selected.id)}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(selected.id)}
                    className="px-3 py-1.5 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Supprimer cette demande
                  </button>
                )}
                <button
                  onClick={handleSaveNote}
                  className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                >
                  <Save className="w-3 h-3" /> Sauvegarder la note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestsTab;
